import React, {useState, useEffect, useRef, useMemo} from 'react';
import { Drawer, Form, Space, Input, InputNumber, Button, message } from 'antd';
import moment from 'moment';
import { encryptTextByAES, decryptTextByAES } from '../../../../utils/encryption';

import SPContact from '../SPContact';
import SPBookingTime from '../SPBookingTime';
import SPBusinessTime from '../SPBusinessTime';
import SPUploadSinglePhoto from '../../../base/SPUploadSinglePhoto';
import SPUploadPhotos from '../../../base/SPUploadPhotos';
import SPMap from '../../../base/SPMap';
import { getStoreDetail, storeCheckName, storeAdd, storeUpdate } from 'hooks/carRental/store';
import useModalCloseConfirm from 'hooks/base/modalCloseConfirm';
import SPTitle from 'components/carRental/base/SPTitle';
import SPTag from 'components/base/SPTag';

import { API_VEHICLE_UPLOAD_PHOTO } from 'hooks/carRental/_';

import emiter, {
  EVENT_CARRENTAL_STORE_EDIT,
  EVENT_CARRENTAL_STORE_VIEW,
  EVENT_CARRENTAL_ADD_STORE,
  EVENT_CARRENTAL_STORE_SEARCH,
} from '../../../../events';
import {changeEnumToText} from "../../../../utils/tool";
import SPMaxTime from "../SPMaxTime";

interface PropsType {
  enums?: any;
}

const getTag = (type: any, text: string) =>  {
  const tagsMap: any = {
    0: <SPTag type="processing" text={text} />,
    1: <SPTag type="success" text={text} />,
    2: <SPTag type="error" text={text} />
  }
  return tagsMap[type];
}

const UPLOAD_SIZE = 10;

const SPEmployeeAccountDrawer: React.FC<PropsType> = props => {
  const { enums } = props;

  const [mainForm] = Form.useForm();
  // const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [type, setType] = useState<string>('');

  const storeInfoRef = useRef<any>({});

  const { visible, onOpen, onClose, onOk, onEdited } = useModalCloseConfirm({});
  const [storeDetail, setStoreDetail] = useState<any>(null);

  const drawerWidth = '70%';

  const mainFormFields = {
    storeName: {
      name: 'store_name',
      label: '门店名称',
      required: true,
      message: '请填写门店名称',
      placeholder: '请输入门店名称',
    },
    contactInfo: {
      name: 'contact_info_encrypt',
      label: '联系方式',
      required: true,
      message: '请填写联系方式',
      placeholder: '请输入联系方式',
    },
    position: {
      name: 'position',
      label: '定位选择',
      required: true,
      message: '请填写定位选择',
      placeholder: '定位选择',
    },
    address: {
      name: 'address',
      label: '门店地址',
      required: true,
      message: '请填写门店地址',
      placeholder: '请填写门店地址',
    },
    leastPreBookingTime: {
      name: 'pre_booking_time',
      label: '最小提前预定时间',
      required: true,
      message: '请输入最小提前预定时间',
      placeholder: '请输入最小提前预定时间',
    },
    maximumBookingTime:{
      name: 'max_pre_booking_time',
      label: '最大提前预定期',
      required: true,
      message: '请输入最大提前预定期',
      placeholder: '请输入最大提前预定期',
    },
    commission: {
      name: 'commission',
      label: '车行手续费',
      required: true,
      message: '请输入车行手续费',
      placeholder: '请输入车行手续费',
    },
    businessTime: {
      name: 'business',
      label: '营业时间',
      required: true,
      message: '请选择营业时间',
      placeholder: '营业时间',
    },
    nightFee: {
      name: 'night_fee',
      label: '夜间服务费',
      required: true,
      message: '请输入夜间服务费',
      placeholder: '请输入夜间服务费',
    },
    qualificationDocList: {
      name: 'qualification_doc_list',
      label: '关系证明',
      required: true,
      message: '请上传关系证明',
      placeholder: '请上传关系证明',
      help:
        <>
          <div>请提交加盖主体公司公章的证明文件；审核时间预计1-3个工作日；</div>
          <div>支持上传 jpg、jpeg、png格式的图片。</div>
        </>
    },
    storeAvatarImg: {
      name: 'store_img',
      label: '门店门头照片',
      required: false,
      message: '请上传门店门头照片',
      placeholder: '门店门头照片',
    },
    envImgs: {
      name: 'env_imgs',
      label: '其他环境照片',
      required: false,
      message: '请上传其他环境照片错误!',
      placeholder: '其他环境照片',
    },
  };

  const onStoreAddModelOpen = () => {
    onOpen();
    mainForm.resetFields();
  };

  const handleClose = () => {
    setDisabled(false);
    setTimeout(() => {
        setStoreDetail(null)
    }, 200)
  };

  useEffect(() => {
    emiter.on(EVENT_CARRENTAL_ADD_STORE, onStoreAdd);
    emiter.on(EVENT_CARRENTAL_STORE_EDIT, onStoreEdit);
    emiter.on(EVENT_CARRENTAL_STORE_VIEW, onStoreView);
    return () => {
        emiter.removeAllListeners(EVENT_CARRENTAL_ADD_STORE)
        emiter.removeAllListeners(EVENT_CARRENTAL_STORE_EDIT)
        emiter.removeAllListeners(EVENT_CARRENTAL_STORE_VIEW)
    }
    // emiter.on(EVENT_CARRENTAL_EDIT_EMPLOYEEACCOUNT, onEdit);
  }, []);

  const EditActionBar = () => {
    return (
      <Space>
        <Button onClick={() => onClose({ callback: handleClose })}>关闭</Button>
        <Button type="primary" onClick={() => onOk({ callback: onFinish })}>提交</Button>
      </Space>
    );
  };

  const AddActionBar = () => {
    return (
      <Space>
        <Button onClick={() => onClose({ callback: handleClose })}>关闭</Button>
        <Button type="primary" onClick={() => onOk({ callback: onFinish })}>
          提交
        </Button>
      </Space>
    );
  };

  const onFinish = (callback: any) => {
    const values = mainForm.getFieldsValue();
    const {
      store_name,
      contact_info_encrypt,
      position,
      address,
      pre_booking_time,
      commission,
      business,
      store_img,
      env_imgs,
      qualification_doc_list,
      max_pre_booking_time
    } = values || {};
    const { time: least_pre_booking_time, unit: least_pre_booking_time_unit } = pre_booking_time || {};
    const { time: business_time, openNightService, pickUpFee, returnFee } = business;
    const [startTime, endTime] = business_time;
    const { city_id, lng, lat } = position || {};

    const emergencyMember = contact_info_encrypt.filter((item: any) => item?.emergency);
    const noEmergencyMember = contact_info_encrypt.filter((item: any) => !item?.emergency);
    const contactInfoEncrypt = encryptTextByAES(
      [...emergencyMember, ...noEmergencyMember].map((item: any) => item?.contact).join(','),
    );

    const params: any = {
      store_name,
      contact_info_encrypt: contactInfoEncrypt,
      city_id,
      longitude: `${lng}`,
      latitude: `${lat}`,
      address,
      max_pre_booking_time,
      commission: commission * 100,
      least_pre_booking_time,
      least_pre_booking_time_unit,
      open_time: startTime.format('HH:mm'),
      end_time: endTime.format('HH:mm'),
      store_img: store_img?.file_addr,
      env_imgs: (env_imgs || []).map((item: any) => item.file_addr),
      qualification_doc_list: (qualification_doc_list || []).map((item: any) => item.file_addr),
    };

    let requestMethod = storeAdd;

    if (storeInfoRef.current?.store_code) {
      params.store_code = storeInfoRef.current.store_code;
      requestMethod = storeUpdate;
    }

    if (openNightService) {
      params.night_pickup_fee = +pickUpFee * 100;
      params.night_return_fee = +returnFee * 100;
    }

    mainForm.validateFields().then(values => {
      requestMethod(params).then((res: any) => {
        const { errno, errmsg } = res || {};
        if (errno === 0) {
          message.success('操作成功')
          if (callback) callback();
          emiter.emit(EVENT_CARRENTAL_STORE_SEARCH, { pageIndex: 1, pageSize: 10 });
        } else {
          message.error(errmsg || '操作失败');
        }
      });
    })
    .catch(() => {
      message.warning('信息填写不全，请完善后提交');
    });
  };

  const handleStoreDetailGet = (data: any) => {
    getStoreDetail({
        store_code: data.store_code,
    }).then((res: any) => {
        const { errno, data } = res || {};
        if (errno === 0) {
          setStoreDetail(data)
          const {
            store_name,
            contact_info_encrypt,
            address,
            least_pre_booking_time,
            least_pre_booking_time_unit,
            commission,
            open_time,
            end_time,
            night_pickup_fee,
            night_return_fee,
            city_id,
            latitude,
            longitude,
            qualification_doc_list,
            store_img,
            env_imgs,
            max_pre_booking_time
          } = data || {};
          const fromData = {
            store_name,
            contact_info_encrypt: decryptTextByAES(contact_info_encrypt)
              .split(',')
              .map((item: string, index: number) => ({ contact: item, emergency: index === 0 })),
            address,
            pre_booking_time: { time: least_pre_booking_time, unit: least_pre_booking_time_unit },
            commission: commission / 100,
            business: {
              time: [moment(open_time, 'HH:mm'), moment(end_time, 'HH:mm')],
              openNightService: !!(night_pickup_fee || night_return_fee),
              pickUpFee: night_pickup_fee / 100,
              returnFee: night_return_fee / 100,
            },
            position: {
              city_id,
              lng: +longitude,
              lat: +latitude,
            },
            max_pre_booking_time,
            qualification_doc_list,
            store_img,
            env_imgs,
          };
          mainForm.setFieldsValue(fromData);
      }
    });
  };

  const generateLabelString = (arr: any) => {
    let labels: any = [];
    function recurse(items: any, prefix = "") {
      if (!items) return;
      for (let item of items) {
        let currentLabel = prefix ? `${prefix}-${item.configReason}` : item.configReason;
        if (item.children && item.children.length > 0) {
          recurse(item.children, currentLabel);
        } else {
          labels.push(currentLabel);
        }
      }
    }
    recurse(arr);
    return labels.join(",");
  }

  const rejectionText = useMemo(() => {
    if (storeDetail) {
      const text = changeEnumToText({
        nameSpace: 'audit_status',
        value: storeDetail?.audit_status,
        enums: enums
      })
      let reason = ''
      if (storeDetail?.labels) {
        reason = generateLabelString(storeDetail?.labels || [])
      }
      return `${text} ${reason ? `:${reason}` : '' }`
    }
  }, [storeDetail, enums])

  const onStoreAdd = () => {
    onStoreAddModelOpen();
    setType('add');
    storeInfoRef.current = {};
  };

  const onStoreEdit = (data: any) => {
    setType('edit');
    onStoreAddModelOpen();
    storeInfoRef.current = data;
    handleStoreDetailGet(data);
  };

  const onStoreView = (data: any) => {
    mainForm.setFieldsValue(data);
    setType('view');
    setDisabled(true);
    onStoreAddModelOpen();
    handleStoreDetailGet(data);
  };

  const handleValuesChange = () => {
    onEdited();
  }

  const drawerTitle = () => {
    const titleMap: any = { view: '查看门店', 'edit': '编辑门店' }
    if (['view', 'edit'].includes(type)) {
      const checkeStatus = enums?.audit_status?.find((item: any) => item.key === storeDetail?.audit_status)?.value;
      const tagText = `${checkeStatus}${storeDetail?.audit_status_desc ? `：${storeDetail?.audit_status_desc}` : ''}`
      return (
        <div style={{ display: 'flex' }}>
          <span style={{ marginRight: 8, wordBreak: "keep-all", display: 'flex', alignItems: 'center'  }}>{titleMap[type]}</span>
          <div style={{ flex: 1 }}>{getTag(storeDetail?.audit_status, tagText)}</div>
        </div>
      )
    }
    if (type === 'add') return '新增门店';
    return ''
  }

  const actionBar = () => {
    if (type === 'add') {
      return <AddActionBar />
    }
    if (type === 'edit') {
      return <EditActionBar />
    }
    return <></>;
  }

  return (
      <>
        <Drawer
          title={drawerTitle()}
          width={drawerWidth}
          onClose={() => onClose({ callback: handleClose })}
          open={visible}
          destroyOnClose
          extra={actionBar()}
        >
          <Form form={mainForm} labelCol={{ style: { width: '138px' } }} disabled={disabled} onValuesChange={handleValuesChange} scrollToFirstError>
            <SPTitle title='基础信息' />
              <Form.Item
                name={mainFormFields.storeName.name}
                label={mainFormFields.storeName.label}
                validateTrigger="onBlur"
                rules={[
                    { required: true },
                    {
                      validator(_, value, callback) {
                        if (!value) {
                        callback('');
                        return;
                      }
                      if (storeInfoRef.current?.store_name === value) {
                        callback();
                        return;
                      }
                      storeCheckName({
                          store_name: value
                      }).then(res => {
                        if (res?.data) {
                          callback();
                        } else {
                          callback('门店名称重复');
                        }
                      }).catch(() => {
                        callback('门店名称重复');
                      })
                    },
                  },
                ]}
              >
                <Input placeholder={mainFormFields.storeName.placeholder} maxLength={100} showCount />
              </Form.Item>
              <Form.Item
                name={mainFormFields.contactInfo.name}
                label={mainFormFields.contactInfo.label}
                rules={[
                  {
                    required: mainFormFields.contactInfo.required,
                    message: mainFormFields.contactInfo.message,
                  },
                ]}
              >
                <SPContact useInput={true} />
              </Form.Item>
              <Form.Item
                name={mainFormFields.position.name}
                label={mainFormFields.position.label}
                rules={[
                  {
                    required: mainFormFields.position.required,
                    message: mainFormFields.position.message,
                  },
                ]}
              >
                <SPMap plugins={['PoiSearch', 'CitySelect']} />
              </Form.Item>
              <Form.Item
                  name={mainFormFields.address.name}
                  label={mainFormFields.address.label}
                  rules={[
                    {
                      required: mainFormFields.address.required,
                      message: mainFormFields.address.message,
                    },
                  ]}
                  help={<>{storeDetail?.audit_passed_address !== storeDetail?.address ? <>修改前的地址：{storeDetail?.audit_passed_address}<span style={{ color: 'red' }}>（修改前的地址会继续展示给用户，如果地址会影响到用户取还，新地址审核通过前，请先下架门店）</span> `</> : "用户到门店取还车时展示的地址"}</>}
                >
                  <Input placeholder={mainFormFields.address.placeholder} maxLength={50} showCount />
              </Form.Item>
              <SPTitle title='服务信息' style={{ paddingTop: 12 }} />
              <Form.Item
                name={mainFormFields.leastPreBookingTime.name}
                label={mainFormFields.leastPreBookingTime.label}
                rules={[
                  { required: true },
                  {
                    validator(_, value, callback) {
                      if (value && !value.time) {
                          callback(mainFormFields.leastPreBookingTime.message);
                            return;
                      }
                      callback();
                    },
                  },
                ]}
              >
                <SPBookingTime placeholder={mainFormFields.leastPreBookingTime.placeholder} />
              </Form.Item>
            <Form.Item
                name={mainFormFields.maximumBookingTime.name}
                label={mainFormFields.maximumBookingTime.label}
                required={mainFormFields.maximumBookingTime.required}
            >
              <SPMaxTime
                  name={mainFormFields.maximumBookingTime.name}
                  required={mainFormFields.maximumBookingTime.required}
                  message={mainFormFields.maximumBookingTime.message}
                  placeholder={mainFormFields.maximumBookingTime.placeholder}
                  min={1}
                  max={300}
                  initialValue={180}
              />
            </Form.Item>
              <Form.Item
                name={mainFormFields.commission.name}
                label={mainFormFields.commission.label}
                rules={[
                  {
                    required: mainFormFields.commission.required,
                    message: mainFormFields.commission.message,
                  },
                ]}
              >
                <InputNumber
                  placeholder={mainFormFields.commission.placeholder}
                  step={1}
                  precision={0}
                  min={1}
                  max={100}
                  style={{ width: '300px' }}
                  addonAfter="元"
                />
              </Form.Item>
              <Form.Item
                name={mainFormFields.businessTime.name}
                label={mainFormFields.businessTime.label}
                rules={[
                  {
                    required: mainFormFields.businessTime.required,
                    message: mainFormFields.businessTime.message,
                  },
                ]}
              >
                <SPBusinessTime />
              </Form.Item>
              <SPTitle title='门店与主体关系证明' style={{ paddingTop: 12 }} />
              <Form.Item
                name={mainFormFields.qualificationDocList.name}
                label={mainFormFields.qualificationDocList.label}
                rules={[
                  {
                    required: mainFormFields.qualificationDocList.required,
                    message: mainFormFields.qualificationDocList.message,
                  },
                ]}
                help={mainFormFields.qualificationDocList.help}
              >
                <SPUploadPhotos accept=".jpg,.jpeg,.png" action={API_VEHICLE_UPLOAD_PHOTO} fileSize={UPLOAD_SIZE} hideTitle maxNum={20} />
              </Form.Item>
              {/* <SPTitle title='门店图片信息' />
              <div>
                <div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }}>
                  <p>1.仅支持jpg、jpeg、png格式的图片，长宽均不可超过2000px（最优比例2:1），{UPLOAD_SIZE}M以内;</p>
                  <p>2.不可上传手机截屏，以及含有清晰人脸、电话、二维码、第三方水印、LOGO的图片</p>
                </div>
              </div>
              <Form.Item
                name={mainFormFields.storeAvatarImg.name}
                label={mainFormFields.storeAvatarImg.label}
                rules={[
                  {
                    required: mainFormFields.storeAvatarImg.required,
                    message: mainFormFields.storeAvatarImg.message,
                  },
                ]}
              >
                  <SPUploadSinglePhoto accept=".jpg,.jpeg,.png" action={API_VEHICLE_UPLOAD_PHOTO} fileSize={UPLOAD_SIZE} hideTitle />
              </Form.Item>
              <Form.Item
                name={mainFormFields.envImgs.name}
                label={mainFormFields.envImgs.label}
                rules={[
                  {
                    required: mainFormFields.envImgs.required,
                    message: mainFormFields.envImgs.message,
                  },
                ]}
              >
                <SPUploadPhotos accept=".jpg,.jpeg,.png" action={API_VEHICLE_UPLOAD_PHOTO} fileSize={UPLOAD_SIZE} hideTitle />
            </Form.Item> */}
          </Form>
      </Drawer>
    </>
  );
};

export default SPEmployeeAccountDrawer;
